import * as React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
// components
import Divider from '@mui/material/Divider'
import PopoverModal from 'components/UI/PopoverModal'
// icons
import { FormIcon, LinkedFormIcon } from 'themes/views/objects'
import MenuItemOrigin from '@mui/material/MenuItem'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
// types
import { FieldType, IField } from 'types/graphql'
// constants
import { NO_VALUE_FIELD_TYPES } from 'constants/objects'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// graphql
import { FIELD_LINK_CREATE_MUTATION } from 'graphql/field/mutations'
// views
import { FieldLabel, Wrapper } from './views'
// utils
import { isLinked } from 'utils/form'
import { handleError } from 'utils/errors/handlers'
import { getIconForProperty } from 'utils/fields'

const MODAL_PROPS = {
  anchorOrigin: {
    vertical: 'top' as const,
    horizontal: 'right' as const
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const
  }
}

interface Props {
  parentFieldId: string
  onFieldSelect: () => void
}

const AddFormFieldMenu: React.FC<Props> = ({ parentFieldId, onFieldSelect }) => {
  const [createFieldLink] = useMutation(FIELD_LINK_CREATE_MUTATION)

  const { riskTemplate, linkedRiskTemplates, refetchTemplate } = React.useContext(RiskTemplateAPIContext)

  const addField = async (field: IField, templateId: string, closePopup: () => void) => {
    try {
      const fieldLinkInput = {
        parentFieldId,
        fieldId: field.id,
        targetEntityId: riskTemplate.id,
        addFieldLabel: field.label,
        fieldLabel: field.label,
        position: riskTemplate.fields.filter((field) => field.parentFieldId === parentFieldId).length,
        primary: false
      }

      await createFieldLink({
        variables: {
          input: fieldLinkInput
        }
      })

      await refetchTemplate()

      closePopup()
      onFieldSelect()
    } catch (e) {
      handleError(e, 'AddFormField addField error')
    }
  }

  const fieldLinks = riskTemplate.fieldLinks.filter((fieldLink) => fieldLink.parentFieldId === parentFieldId)

  if (!fieldLinks.length) {
    return null
  }

  const linkedRiskTemplatesMap = linkedRiskTemplates
    .filter(({ fields }) => fields.some((field) => fieldLinks.some(({ field: { id } }) => field.id === id)))
    .map((item) => {
      const fieldLink = fieldLinks.find((fieldLink) => fieldLink.primary && fieldLink.field.entityId === item.id)
      const linked = isLinked(fieldLink?.linkingSettings)

      const fields = item.fields
        .filter((field: IField) => field.active && !NO_VALUE_FIELD_TYPES.includes(field.fieldType))
        .map((field) => {
          const isAdded = fieldLinks.some((fieldLink) => fieldLink.field.id === field.id)

          if (isAdded) {
            return {
              ...field,
              disabled: true
            }
          }

          return field
        })

      return { ...item, fields, linked }
    })

  return (
    <>
      {linkedRiskTemplatesMap.map(({ name, fields, id, linked }, idx) => (
        <MenuItemOrigin key={idx}>
          <Wrapper>
            {linked ? <LinkedFormIcon /> : <FormIcon />}
            <FieldLabel>{name}</FieldLabel>
            <PopoverModal {...MODAL_PROPS} trigger={<ArrowForwardIosIcon fontSize='small' />}>
              {({ closePopup }) => (
                <>
                  {fields.map((field) => (
                    <MenuItemOrigin
                      key={field.id}
                      // @ts-ignore
                      disabled={field.disabled}
                      onClick={() => addField(field, id, closePopup)}>
                      {getIconForProperty(field.fieldType)}
                      <FieldLabel>{field.label}</FieldLabel>
                      {field.fieldType === FieldType.Table && <ArrowForwardIosIcon fontSize='small' />}
                    </MenuItemOrigin>
                  ))}
                </>
              )}
            </PopoverModal>
          </Wrapper>
        </MenuItemOrigin>
      ))}
      <Divider variant='middle' />
    </>
  )
}

export default AddFormFieldMenu
